import { toast } from "react-toastify";
import NotificationComponent from "../../components/Notification/Notification";

interface NotificationProps {
  status?: "success" | "error" | "info";
  title: string;
  description: string;
  acceptText?: string;
  acceptHandler?: () => void;
  rejectText?: string;
  rejectHandler?: () => void;
  iconSrc?: string;
}

const lastToastIds: Record<string, string | number> = {};
const minTime = 7000;
const maxTime = 15000;
const minLength = 1;
const maxLength = 91;

function findShowTime(description: string) {
  const descriptionLength = description.length;
  const showTime =
    descriptionLength <= maxLength
      ? minTime +
      (maxTime - minTime) *
      ((descriptionLength - minLength) / (maxLength - minLength))
      : maxTime;
  return showTime;
}

function ShowNotification({
  type = 'toast',
  status,
  title,
  description,
  iconSrc,
  acceptText,
  rejectText,
  acceptHandler,
  rejectHandler,

}: NotificationProps & { type?: 'toast' | 'dialog' }) {

  const notificationKey = type === 'dialog'
    ? `dialog_${title}_${description}_${acceptText}_${rejectText}`
    : `toast_${status}_${title}_${description}`;

  const showTime = findShowTime(description);
  const existingToastId = lastToastIds[notificationKey];

  if (existingToastId && toast.isActive(existingToastId)) {
    toast.update(existingToastId, {
      render: (
        <NotificationComponent
          closeToast={() => {
            toast.dismiss(existingToastId);
            delete lastToastIds[notificationKey];
          }}
          status={status}
          title={title}
          description={description}
          iconSrc={iconSrc}
          acceptText={acceptText}
          rejectText={rejectText}
          acceptHandler={acceptHandler}
          rejectHandler={rejectHandler}
          highlightToken={Date.now()} // триггерим анимацию
        />
      ),
      autoClose: type === "dialog" ? false : showTime,
    });
    return;
  }

  const toastId = toast(
    <NotificationComponent
      closeToast={() => {
        toast.dismiss(toastId);
        delete lastToastIds[notificationKey];
      }}
      status={status}
      title={title}
      description={description}
      acceptText={acceptText}
      rejectText={rejectText}
      rejectHandler={
        type === "dialog"
          ? () => {
            toast.dismiss(toastId);
            delete lastToastIds[notificationKey];
          }
          : rejectHandler
      }
      acceptHandler={
        type === "dialog"
          ? () => {
            acceptHandler?.();
            toast.dismiss(toastId);
            delete lastToastIds[notificationKey];
          }
          : acceptHandler
      }

    />,
    {
      hideProgressBar: true,
      closeButton: false,
      autoClose: type === "dialog" ? false : showTime,
      onClose: () => {
        delete lastToastIds[notificationKey];
      },
    }
  );

  lastToastIds[notificationKey] = toastId;
}

export function displayNotify({
  status,
  title,
  description,
}: NotificationProps) {
  ShowNotification({
    type: 'toast',
    status,
    title,
    description,
  });
}

export function dialogNotify({
  status,
  title,
  description,
  acceptHandler,
  acceptText,
  rejectText,
}: NotificationProps) {
  ShowNotification({
    type: 'dialog',
    status,
    title,
    description,
    acceptHandler,
    acceptText,
    rejectText,
  });
}

export function displayNotImplemented() {
  displayNotify({
    status: "info",
    title: "Киберштаб",
    description: "В разработке",
  });
}

export function displayError(description?: string) {
  displayNotify({
    status: "error",
    title: "Ошибка",
    description: description || "Ошибка",
  });
}

export function displaySuccesCopyLink() {
  displayNotify({
    status: "success",
    title: "Скопировано",
    description: "Отправьте ссылку друзьям",
  });
}

export function displaySuccessUpdate() {
  displayNotify({
    status: "success",
    title: "Изменения сохранены",
    description: "Информация профиля обновлена",
  });
}

export function displaySuccessCreate() {
  displayNotify({
    status: "success",
    title: "Данные сохранены",
    description: "Команда успешно создана",
  });
}

export function displayNotSaved() {
  displayNotify({
    status: "error",
    title: "Данные не сохранены",
    description: "Для смены вкладок сохраните изменения",
  });
}

export function displayInfoMessage() {
  displayNotify({
    status: "info",
    title: "Инфо",
    description: "Это информационное сообщение",
  });
}

export function displayStandartDialog() {
  dialogNotify({
    status: "success",
    title: "Это диалог",
    description: "Всё хорошо?",
    acceptText: "Хорошо",
    rejectText: "Нет",
  });
}

export function displayCloseModalDialog(acceptHandler: () => void, description?:string) {
  dialogNotify({
    status: "info",
    title: "Закрытие окна",
    description: description? description: "Данные не сохранятся. Закрыть окно?",
    acceptText: "Хорошо",
    rejectText: "Нет",
    acceptHandler,
  });
}

export function displaySuccessMessage(description: string) {
  displayNotify({
    status: "success",
    title: "Данные отправлены",
    description: description,
  });
}

export function displayNoConnection() {
  displayNotify({
    status: "error",
    title: "Нет соединения",
    description: "Проверьте интернет или попробуйте позже.",
  });
}